import React, { useState, useMemo } from 'react';
import { Link, useNavigate, generatePath } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import {
  Edit3,
  Trash2,
  PauseCircle,
  PlayCircle,
  Package,
  Plus,
} from 'lucide-react';
import {
  useUpdateFirestoreItem,
  useDeleteFirebaseItem,
  useDeleteImages,
} from '../../hooks';
import {
  LISTING_ITEM_STATUSES,
  categoryMessages,
  ROUTES,
} from '../../constants';
import { ImagePlaceholder } from '../../components/ImagePlaceholder';
import { Modal } from '../../components/Modal.tsx';

// Types
interface Service {
  id: number;
  title: string;
  images: string[];
  category: string;
  price: number;
  description: string;
  status: ServiceStatus;
  createdAt: Date;
  updatedAt: Date;
}

type ServiceStatus =
  typeof LISTING_ITEM_STATUSES[keyof typeof LISTING_ITEM_STATUSES];

interface ServicesTabProps {
  userData: any;
  services: Service[];
  isPending: boolean;
  queryFilters: any[];
}

// Utility Functions
const getStatusBadge = (status: ServiceStatus) => {
  switch (status) {
    case LISTING_ITEM_STATUSES.active:
      return 'text-green-600';
    case LISTING_ITEM_STATUSES.paused:
      return 'text-yellow-600';
    case LISTING_ITEM_STATUSES.awaitingApproval:
      return 'text-blue-600';
    case LISTING_ITEM_STATUSES.rejected:
      return 'text-red-600';
    default:
      return '';
  }
};

const getStatusText = (status: ServiceStatus) => {
  switch (status) {
    case LISTING_ITEM_STATUSES.active:
      return <FormattedMessage id='active' defaultMessage='Активно' />;
    case LISTING_ITEM_STATUSES.paused:
      return <FormattedMessage id='paused' defaultMessage='Приостановлено' />;
    case LISTING_ITEM_STATUSES.awaitingApproval:
      return <FormattedMessage id='pending' defaultMessage='На модерации' />;
    case LISTING_ITEM_STATUSES.rejected:
      return <FormattedMessage id='rejected' defaultMessage='Отклонено' />;
    default:
      return '';
  }
};

const getStatusPriority = (status: ServiceStatus): number => {
  switch (status) {
    case LISTING_ITEM_STATUSES.active:
      return 1;
    case LISTING_ITEM_STATUSES.paused:
      return 2;
    case LISTING_ITEM_STATUSES.awaitingApproval:
      return 3;
    case LISTING_ITEM_STATUSES.rejected:
      return 4;
    default:
      return 5;
  }
};

// Component sections
const ServicesSkeleton = () => (
  <div className='space-y-6'>
    {[1, 2].map((index) => (
      <div key={index} className='bg-white rounded-lg shadow-sm p-6'>
        <div className='flex items-start space-x-4'>
          <div className='w-32 h-32 bg-gray-200 rounded-lg animate-pulse' />
          <div className='flex-1'>
            <div className='flex items-start justify-between'>
              <div className='space-y-2'>
                <div className='h-6 bg-gray-200 rounded w-48 animate-pulse' />
                <div className='h-4 bg-gray-200 rounded w-32 animate-pulse' />
                <div className='h-5 bg-gray-200 rounded w-24 animate-pulse' />
              </div>
              <div className='flex space-x-2'>
                {[1, 2, 3].map((btn) => (
                  <div
                    key={btn}
                    className='w-8 h-8 bg-gray-200 rounded animate-pulse'
                  />
                ))}
              </div>
            </div>
            <div className='mt-4 space-y-2'>
              <div className='h-4 bg-gray-200 rounded w-full animate-pulse' />
              <div className='h-4 bg-gray-200 rounded w-3/4 animate-pulse' />
            </div>
            <div className='flex space-x-4 mt-4'>
              {[1, 2, 3].map((stat) => (
                <div
                  key={stat}
                  className='h-4 bg-gray-200 rounded w-24 animate-pulse'
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className='text-center py-12'>
    <Package className='w-16 h-16 mx-auto text-gray-400 mb-4' />
    <h3 className='text-lg font-medium text-gray-900 mb-2'>
      <FormattedMessage
        id='noServices'
        defaultMessage='У вас пока нет размещенных услуг'
      />
    </h3>
    <p className='text-gray-500 mb-6'>
      <FormattedMessage
        id='startAddingServices'
        defaultMessage='Начните добавлять свои услуги прямо сейчас'
      />
    </p>
    <Link
      to='/add-service'
      className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
    >
      <Plus className='w-4 h-4 mr-2' />
      <FormattedMessage
        id='addFirstService'
        defaultMessage='Добавить первую услугу'
      />
    </Link>
  </div>
);

// Status section header
const StatusSectionHeader = ({ status }: { status: ServiceStatus }) => (
  <div className='mb-4 mt-8 border-b pb-2'>
    <h2 className={`font-medium text-lg ${getStatusBadge(status)}`}>
      {getStatusText(status)}
    </h2>
  </div>
);

// Main component
export const ServicesTab: React.FC<ServicesTabProps> = ({
  services,
  isPending,
  queryFilters,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [serviceIdToDelete, setServiceIdToDelete] = useState(null);

  // Mutations
  const { mutate: updateServiceItemStatus } = useUpdateFirestoreItem();
  const { mutate: deleteImages } = useDeleteImages();
  const { mutate: deleteServiceMutation } = useDeleteFirebaseItem();

  // Group services by status and sort statuses by priority
  const servicesByStatus = useMemo(() => {
    if (!services) return [];

    // Sort services by status priority
    const sortedServices = [...services].sort(
      (a, b) => getStatusPriority(a.status) - getStatusPriority(b.status),
    );

    // Group by status
    const grouped = sortedServices.reduce((acc, service) => {
      if (!acc[service.status]) {
        acc[service.status] = [];
      }
      acc[service.status].push(service);
      return acc;
    }, {} as Record<ServiceStatus, Service[]>);

    // Convert to array of groups
    return Object.entries(grouped) as [ServiceStatus, Service[]][];
  }, [services]);

  const ServiceCard = ({ service }: { service: Service }) => {
    const toggleServiceStatus = () => {
      updateServiceItemStatus({
        filters: queryFilters,
        id: service.id.toString(),
        newData: {
          status:
            service.status === LISTING_ITEM_STATUSES.active
              ? LISTING_ITEM_STATUSES.paused
              : LISTING_ITEM_STATUSES.active,
        },
      });
    };

    const deleteService = (id: string) => {
      setShowDeleteServiceModal(true);
      setServiceIdToDelete(id);
    };

    const hasImages = service.images && service.images.length > 0;

    // Determine if we should allow navigation to service details
    const shouldAllowNavigation = service.status !== LISTING_ITEM_STATUSES.awaitingApproval;
    
    // Use a div instead of Link for items awaiting approval
    const CardWrapper = shouldAllowNavigation
      ? ({ children }: { children: React.ReactNode }) => (
          <Link
            to={`/services/${service.id}`}
            className='bg-white rounded-lg shadow-sm p-4 sm:p-6 d-block block hover:shadow-md transition-shadow'
          >
            {children}
          </Link>
        )
      : ({ children }: { children: React.ReactNode }) => (
          <div className='bg-white rounded-lg shadow-sm p-4 sm:p-6 cursor-not-allowed opacity-90 relative'>
            {children}
          </div>
        );
    
    return (
      <CardWrapper>
        <div key={service.id}>
          <div className='flex flex-col sm:flex-row sm:items-start gap-4'>
            {hasImages ? (
              <img
                src={service.images[0]}
                alt={service.title}
                className='w-full sm:w-32 h-48 sm:h-32 rounded-lg object-cover'
              />
            ) : (
              <ImagePlaceholder />
            )}
            <div className='flex-1 min-w-0'>
              <div className='flex flex-col sm:flex-row sm:items-start justify-between gap-4'>
                <div>
                  <h3 className='text-lg font-semibold truncate'>
                    {service.title}
                  </h3>
                  <p className='text-gray-600'>
                    {formatMessage(
                      categoryMessages[`category${service?.category}`],
                    )}
                  </p>
                  <p className='text-blue-600 font-semibold'>
                    {service.price}RON
                  </p>
                </div>
                <div className='flex gap-2'>
                  {![
                    LISTING_ITEM_STATUSES.awaitingApproval,
                    LISTING_ITEM_STATUSES.rejected,
                  ].includes(service.status) && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleServiceStatus();
                      }}
                      className={`p-2 rounded-lg ${
                        service.status === LISTING_ITEM_STATUSES.paused
                          ? 'text-yellow-600 hover:bg-yellow-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={
                        service.status === LISTING_ITEM_STATUSES.active
                          ? formatMessage({
                              id: 'pause',
                              defaultMessage: 'Приостановить',
                            })
                          : formatMessage({
                              id: 'activate',
                              defaultMessage: 'Активировать',
                            })
                      }
                    >
                      {service.status === LISTING_ITEM_STATUSES.active ? (
                        <PauseCircle />
                      ) : (
                        <PlayCircle />
                      )}
                    </button>
                  )}
                  {![LISTING_ITEM_STATUSES.awaitingApproval].includes(
                    service.status,
                  ) && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          generatePath(ROUTES.editService, {
                            id: service.id,
                          }),
                          {
                            state: {
                              service,
                              filters: queryFilters,
                              prevRoute: ROUTES.profile,
                            },
                          },
                        );
                      }}
                      className='p-2 text-blue-600 hover:bg-blue-50 rounded-lg'
                      title={formatMessage({
                        id: 'edit',
                        defaultMessage: 'Редактировать',
                      })}
                    >
                      <Edit3 />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      deleteService(service.id.toString());
                    }}
                    className='p-2 text-red-600 hover:bg-red-50 rounded-lg'
                    title={formatMessage({
                      id: 'remove',
                      defaultMessage: 'Удалить',
                    })}
                  >
                    <Trash2 />
                  </button>
                </div>
              </div>
              <p className='text-gray-600 mt-2 truncate'>
                {service.description}
              </p>
              <div className='flex flex-wrap gap-4 mt-1 text-sm text-gray-500'>
                <span className={getStatusBadge(service.status)}>
                  {getStatusText(service.status)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardWrapper>
    );
  };

  if (isPending) {
    return <ServicesSkeleton />;
  }

  if (!services || services.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div>
        {servicesByStatus.map(([status, statusServices], index) => (
          <div
            key={status}
            className={classNames({ 'first:mt-0': index === 0 })}
          >
            <StatusSectionHeader status={status} />
            <div className='space-y-6'>
              {statusServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {showDeleteServiceModal && (
        <Modal
          title={formatMessage({
            id: 'userProfile.deleteService',
            defaultMessage: 'Удаление',
          })}
          isOpen={true}
          onClose={() => setShowDeleteServiceModal(false)}
        >
          {formatMessage({
            id: 'areYouSureToRemoveItem',
            defaultMessage: 'Вы уверены, что хотите удалить эту услугу?',
          })}
          <div className='flex items-center space-x-2'>
            <button
              type='button'
              onClick={() => setShowDeleteServiceModal(false)}
              className='mt-4 px-6 py-2 text-white rounded-lg focus:outline-none bg-blue-600 hover:bg-blue-700'
            >
              <FormattedMessage
                id='addService.successMessageDetailsButton.back'
                defaultMessage='Назад'
              />
            </button>

            <button
              onClick={() => {
                if (serviceIdToDelete) {
                  const targetService = services?.find(
                    ({ id }) => id.toString() === serviceIdToDelete,
                  );
                  deleteImages(targetService?.images);
                  deleteServiceMutation({
                    id: serviceIdToDelete,
                    filters: queryFilters,
                  });
                  setShowDeleteServiceModal(false);
                }
              }}
              type='button'
              className='mt-4 px-6 py-2 text-white rounded-lg focus:outline-none bg-red-600 hover:bg-red-700'
            >
              <FormattedMessage id='remove' defaultMessage='Удалить' />
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
